'use client';

/**
 * ============================================================================
 * DATE UTILITIES - TIMEZONE HANDLING STRATEGY
 * ============================================================================
 *
 * IMPORTANT: Backend handles ALL timezone conversions to CST.
 *
 * Strategy:
 * - Backend sends: Dates/times already converted to CST
 * - Frontend: Parse and display literally WITHOUT timezone conversion
 * - Frontend sends: Simple date/time format (backend converts to CST)
 *
 * DO NOT:
 * - Convert timezones in frontend
 * - Use getUTC* methods
 * - Add/subtract timezone offsets
 *
 * DO:
 * - Parse dates literally as received
 * - Format for display using local methods
 * - Trust backend's timezone handling
 *
 * Note: Some function names contain "UTC" for historical reasons,
 * but they now parse dates literally without timezone conversion.
 * ============================================================================
 */
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
export const CST_TIMEZONE = 'America/Chicago'; // CST/CDT timezone

import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  differenceInMonths,
  endOfMonth,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isToday,
  isTomorrow,
  isValid,
  isYesterday,
  parse as parseDateFns, // ← Use alias to avoid conflict,
  parseISO,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';

// ===== CONSTANTS =====
const DATE_FORMATS = {
  ISO: 'yyyy-MM-dd',
  DISPLAY: 'MMM dd, yyyy',
  SHORT: 'MM/dd/yyyy',
  SHORT_US: 'M/d/yyyy',
  LONG: 'MMMM dd, yyyy',
  TIME: 'HH:mm:ss',
  TIME_12H: 'hh:mm:ss a',
  DATETIME: 'yyyy-MM-dd HH:mm:ss',
  DATETIME_PIPE: 'yyyy-MM-dd | hh:mm:ss a',
  DATETIME_12H: 'yyyy-MM-dd hh:mm:ss a',
  COMPACT: 'yyyyMMdd',
  READABLE: 'EEEE, MMMM dd, yyyy',
  UTCDATETIME: 'yyyy-MM-dd HH:mm',
  US_DATETIME: 'M/d/yyyy h:mm:ss a',
  US_DATETIME_FULL: 'MM/dd/yyyy HH:mm',
  US_DATETIME_WITH_SECONDS: 'MM/dd/yyyy hh:mm:ss a',
  INTL_DATE: 'yyyy/MM/dd',
  INTL_DATETIME: 'yyyy/MM/dd hh:mm:ss a',
} as const;

/**
 * Parses date string from backend.
 * - Dates ending with 'Z': Converts UTC to CST
 * - Other dates: Parses literally (already CST)
 * (Name kept for compatibility)
 */
export function parseUTCDate(dateValue: string | Date | null | undefined): Date | null {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    return isValid(dateValue) ? dateValue : null;
  }

  if (typeof dateValue === 'string') {
    // Check if it's a UTC date
    const isUTC = dateValue.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateValue);

    if (isUTC) {
      const utcDate = parseISO(dateValue);
      if (isValid(utcDate)) {
        return toZonedTime(utcDate, CST_TIMEZONE);
      }
      return null;
    }

    // Parse CST dates literally
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateValue);
    if (dateMatch) {
      const [_, year, month, day] = dateMatch;
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0);
    }
  }

  return null;
}

/**
 * Formats date as yyyy-MM-dd.
 * - If date ends with 'Z': Converts UTC to CST first
 * - Otherwise: Formats as-is (already CST)
 * (Name kept for compatibility)
 */
export function formatDateAsUTC(dateValue: string | Date | null | undefined): string {
  if (!dateValue) return '';

  // If already in yyyy-MM-dd format, return as-is
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
    return dateValue;
  }

  // Check if it's a UTC string
  if (typeof dateValue === 'string' && (dateValue.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateValue))) {
    const utcDate = parseISO(dateValue);
    if (isValid(utcDate)) {
      return formatInTimeZone(utcDate, CST_TIMEZONE, 'yyyy-MM-dd');
    }
    return '';
  }

  let date: Date;

  if (typeof dateValue === 'string') {
    const parsed = safeParseDateInput(dateValue);
    if (!parsed) return '';
    date = parsed;
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    return '';
  }

  if (!isValid(date)) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Extracts date part from date string.
 * - If date ends with 'Z': Converts UTC to CST first
 * - Otherwise: Extracts date part as-is (already CST)
 */
export const formatUtcDate = (isoString: string | undefined | null) => {
  if (!isoString) return '';

  // Check if it's a UTC date
  const isUTC = isoString.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(isoString);

  if (isUTC) {
    // Convert UTC to CST, then extract date
    const utcDate = parseISO(isoString);
    if (isValid(utcDate)) {
      return formatInTimeZone(utcDate, CST_TIMEZONE, 'yyyy-MM-dd');
    }
  }

  // Extract date part literally (already CST)
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoString);
  if (dateMatch) {
    return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
  }

  return '';
};

// ===== SAFE DATE PARSING HELPER =====

function parseUtcString(date: string): Date | null {
  const utcDate = parseISO(date);
  if (isValid(utcDate)) {
    return toZonedTime(utcDate, CST_TIMEZONE);
  }
  return null;
}

function parseDateOnlyString(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0);
}

function parseDateTimeString(date: string): Date {
  if (date.includes('T')) {
    return new Date(date); // Parse as local time
  }
  // Handle space-separated format: "2025-01-27 14:30:00"
  const [datePart, timePart] = date.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = timePart?.split(':').map(Number) || [0, 0, 0];
  return new Date(year, month - 1, day, hours, minutes, seconds || 0);
}

function parseStringDate(date: string): Date | null {
  // Check if it's a UTC date (ends with 'Z')
  const isUTC = date.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(date);

  if (isUTC) {
    return parseUtcString(date);
  }

  // For date-only strings (yyyy-MM-dd), parse as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return parseDateOnlyString(date);
  }

  // For datetime strings from backend (already in CST)
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(date)) {
    return parseDateTimeString(date);
  }

  // Try parsing other formats
  const parsed = parseISO(date);
  return isValid(parsed) ? parsed : null;
}

/**
 * Safely parses date input with proper timezone handling
 * @param date - The date input to parse
 * @param preserveTime - Whether to preserve time component (for datetime formats)
 * @returns Parsed Date object or null
 */
/**
 * Parses date input with CST timezone handling.
 * - Dates ending with 'Z' are converted from UTC to CST
 * - Other dates are parsed literally (already in CST from backend)
 */
function safeParseDateInput(date: DateInput | null | undefined): Date | null {
  if (!date) return null;

  // Already a valid Date object
  if (date instanceof Date && isValid(date)) {
    return date;
  }

  // Handle string dates
  if (typeof date === 'string') {
    const parsedString = parseStringDate(date);
    if (parsedString) return parsedString;
  }

  // Handle timestamps
  if (typeof date === 'number') {
    const d = new Date(date);
    if (isValid(d)) return d;
  }

  return null;
}

function getCSTNow(): Date {
  return toZonedTime(new Date(), CST_TIMEZONE);
}

// ===== CORE DATE UTILITIES =====
export const dateUtils = {
  // Format constants
  formats: DATE_FORMATS,

  // Current date in different formats
  timestamp: () => Date.now(),

  today: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.ISO),
  todayDisplay: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.DISPLAY),
  todayLong: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.LONG),
  now: () => getCSTNow(),
  nowISO: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.ISO),
  nowDateTime: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.DATETIME),
  nowDateTimePipe: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.DATETIME_PIPE),
  nowUTC: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.UTCDATETIME),
  datetime_12h: () => formatInTimeZone(new Date(), CST_TIMEZONE, DATE_FORMATS.DATETIME_12H),

  // Previous periods (your current use case)
  prevMonth: () => formatInTimeZone(addDays(subMonths(getCSTNow(), 1), 1), CST_TIMEZONE, DATE_FORMATS.ISO),
  prevWeek: () => formatInTimeZone(subDays(getCSTNow(), 7), CST_TIMEZONE, DATE_FORMATS.ISO),
  yesterday: () => formatInTimeZone(subDays(getCSTNow(), 1), CST_TIMEZONE, DATE_FORMATS.ISO),

  // Next periods
  nextMonth: () => formatInTimeZone(addDays(addMonths(getCSTNow(), 1), 1), CST_TIMEZONE, DATE_FORMATS.ISO),
  nextWeek: () => formatInTimeZone(addDays(getCSTNow(), 7), CST_TIMEZONE, DATE_FORMATS.ISO),
  tomorrow: () => formatInTimeZone(addDays(getCSTNow(), 1), CST_TIMEZONE, DATE_FORMATS.ISO),

  // Common date ranges
  ranges: {
    // Last 7 days
    last7Days: (): DateRange => ({
      fromDate: formatInTimeZone(subDays(getCSTNow(), 7), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Last 30 days
    last30Days: (): DateRange => ({
      fromDate: formatInTimeZone(subDays(getCSTNow(), 30), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Last month with your specific logic
    lastMonth: (): DateRange => ({
      fromDate: formatInTimeZone(addDays(subMonths(getCSTNow(), 1), 1), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Current month
    currentMonth: (): DateRange => ({
      fromDate: formatInTimeZone(startOfMonth(getCSTNow()), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(endOfMonth(getCSTNow()), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Last 3 months
    last3Months: (): DateRange => ({
      fromDate: formatInTimeZone(subMonths(getCSTNow(), 3), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Last 6 months
    last6Months: (): DateRange => ({
      fromDate: formatInTimeZone(subMonths(getCSTNow(), 6), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Last year
    lastYear: (): DateRange => ({
      fromDate: formatInTimeZone(subYears(getCSTNow(), 1), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Current year
    currentYear: (): DateRange => ({
      fromDate: formatInTimeZone(startOfYear(getCSTNow()), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(endOfYear(getCSTNow()), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    nextYearFromToday: (): DateRange => ({
      fromDate: formatInTimeZone(getCSTNow(), CST_TIMEZONE, DATE_FORMATS.ISO),
      toDate: formatInTimeZone(addYears(getCSTNow(), 1), CST_TIMEZONE, DATE_FORMATS.ISO),
    }),

    // Custom range
    custom: (fromDate: DateInput, toDate: DateInput): DateRange => {
      let formattedFromDate = '';
      if (fromDate) {
        const parsedFromDate = typeof fromDate === 'string' ? parseISO(fromDate) : fromDate;
        formattedFromDate = formatInTimeZone(parsedFromDate, CST_TIMEZONE, DATE_FORMATS.ISO);
      }

      let formattedToDate = '';
      if (toDate) {
        const parsedToDate = typeof toDate === 'string' ? parseISO(toDate) : toDate;
        formattedToDate = formatInTimeZone(parsedToDate, CST_TIMEZONE, DATE_FORMATS.ISO);
      }

      return {
        fromDate: formattedFromDate,
        toDate: formattedToDate,
      };
    },
  },

  // Formatting utilities
  format: {
    // Date-only formats (ignore time component to avoid timezone issues)
    iso: (date: DateInput) => {
      if (!date) return '';

      // If already formatted, return as-is
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }

      const parsed = safeParseDateInput(date);
      return parsed ? format(parsed, DATE_FORMATS.ISO) : '';
    },

    display: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.DISPLAY) : '';
    },

    long: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.LONG) : '';
    },

    short: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.SHORT) : '';
    },

    readable: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.READABLE) : '';
    },

    // Date-time formats (preserve time component)
    datetime: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.DATETIME) : '';
    },

    time: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.TIME) : '';
    },

    time12h: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.TIME_12H) : '';
    },

    // Additional datetime formats
    datetimePipe: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.DATETIME_PIPE) : '';
    },

    usDateTime: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.US_DATETIME) : '';
    },

    usDateTimeFull: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.US_DATETIME_FULL) : '';
    },

    usDateTimeWithSeconds: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.US_DATETIME_WITH_SECONDS) : '';
    },

    shortUS: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.SHORT_US) : '';
    },

    intlDate: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.INTL_DATE) : '';
    },

    intlDateTime: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.INTL_DATETIME) : '';
    },

    datetime12h: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Preserve time
      return parsed ? format(parsed, DATE_FORMATS.DATETIME_12H) : '';
    },
    datetime12hUTC: (date: DateInput) => {
      if (!date) return '';

      const dateStr = String(date);
      const isUTC = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);

      if (isUTC) {
        // Convert UTC to CST
        const utcDate = parseISO(dateStr);
        if (!isValid(utcDate)) return '';
        return formatInTimeZone(utcDate, CST_TIMEZONE, 'yyyy-MM-dd hh:mm a');
      }

      // Already CST, parse and format
      const d = safeParseDateInput(dateStr);
      if (!d || !isValid(d)) return '';
      return format(d, 'yyyy-MM-dd hh:mm a');
    },
    compact: (date: DateInput) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date); // Don't preserve time
      return parsed ? format(parsed, DATE_FORMATS.COMPACT) : '';
    },
    // Custom format (let user decide about time preservation)
    custom: (date: DateInput, formatString: string) => {
      if (!date) return '';
      const parsed = safeParseDateInput(date);
      return parsed ? format(parsed, formatString) : '';
    },

    // Converts a date input into ISO format (yyyy-MM-dd)
    slashToIso: (date: DateInput) => {
      if (!date) return '';
      return format(new Date(date), DATE_FORMATS.ISO);
    },
    /**
     * Formats datetime from backend.
     * - Dates ending with 'Z': Converts UTC to CST
     * - Other dates: Formats literally (already CST)
     * (Name kept for compatibility)
     */
    utcToLocal: (utcDate: DateInput, outputFormat: string = DATE_FORMATS.DATETIME_12H) => {
      if (!utcDate) return '';

      try {
        const dateStr = String(utcDate);

        // Check if it's a UTC date
        const isUTC = dateStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(dateStr);

        if (isUTC) {
          // Convert UTC to CST
          const utcDateObj = parseISO(dateStr);
          if (isValid(utcDateObj)) {
            return formatInTimeZone(utcDateObj, CST_TIMEZONE, outputFormat);
          }
          return '';
        }

        // Handle CST dates (no 'Z')
        let localDate: Date;

        // Handle "yyyy-MM-dd hh:mm:ss a" format (12-hour with AM/PM)
        if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}:\d{2} (AM|PM)$/i.test(dateStr)) {
          const parsed = parseDateFns(dateStr, 'yyyy-MM-dd hh:mm:ss a', new Date());
          localDate = parsed;
        }
        // Handle "yyyy-MM-dd HH:mm:ss" format (24-hour)
        else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
          const [datePart, timePart] = dateStr.split(' ');
          const [year, month, day] = datePart.split('-').map(Number);
          const [hours, minutes, seconds] = timePart.split(':').map(Number);
          localDate = new Date(year, month - 1, day, hours, minutes, seconds);
        }
        // Handle ISO format without Z: "2025-01-15T10:30:00"
        else if (dateStr.includes('T')) {
          const [datePart, timePart] = dateStr.split('T');
          const [year, month, day] = datePart.split('-').map(Number);
          const [hours, minutes, seconds] = timePart.split(':').map(Number);
          localDate = new Date(year, month - 1, day, hours, minutes, seconds);
        }
        // Fallback
        else {
          localDate = safeParseDateInput(dateStr) || new Date();
        }

        if (!isValid(localDate)) return '';

        return format(localDate, outputFormat);
      } catch {
        return '';
      }
    },
  },

  // Validation utilities
  validate: {
    isValidDate: (date: DateInput) => {
      const parsed = safeParseDateInput(date);
      return parsed !== null && isValid(parsed);
    },
    isAfter: (date1: DateInput, date2: DateInput) => {
      const parsed1 = safeParseDateInput(date1);
      const parsed2 = safeParseDateInput(date2);
      if (!parsed1 || !parsed2) return false;
      return isAfter(parsed1, parsed2);
    },
    isBefore: (date1: DateInput, date2: DateInput) => {
      const parsed1 = safeParseDateInput(date1);
      const parsed2 = safeParseDateInput(date2);
      if (!parsed1 || !parsed2) return false;
      return isBefore(parsed1, parsed2);
    },
    isSameDay: (date1: DateInput, date2: DateInput) => {
      const parsed1 = safeParseDateInput(date1);
      const parsed2 = safeParseDateInput(date2);
      if (!parsed1 || !parsed2) return false;
      return isSameDay(parsed1, parsed2);
    },
    isToday: (date: DateInput) => {
      const parsed = safeParseDateInput(date);
      return parsed ? isToday(parsed) : false;
    },
    isYesterday: (date: DateInput) => {
      const parsed = safeParseDateInput(date);
      return parsed ? isYesterday(parsed) : false;
    },
    isTomorrow: (date: DateInput) => {
      const parsed = safeParseDateInput(date);
      return parsed ? isTomorrow(parsed) : false;
    },
  },

  // Calculation utilities
  calculate: {
    daysBetween: (startDate: DateInput, endDate: DateInput) => {
      const start = safeParseDateInput(startDate);
      const end = safeParseDateInput(endDate);
      if (!start || !end) return 0;
      return differenceInDays(end, start);
    },

    monthsBetween: (startDate: DateInput, endDate: DateInput) => {
      const start = safeParseDateInput(startDate);
      const end = safeParseDateInput(endDate);
      if (!start || !end) return 0;
      return differenceInMonths(end, start);
    },

    addDays: (date: DateInput, days: number) => {
      const parsed = safeParseDateInput(date);
      if (!parsed) return '';
      const result = addDays(parsed, days);
      return formatInTimeZone(result, CST_TIMEZONE, DATE_FORMATS.ISO);
    },

    subtractDays: (date: DateInput, days: number) => {
      const parsed = safeParseDateInput(date);
      if (!parsed) return '';
      return formatInTimeZone(subDays(parsed, days), CST_TIMEZONE, DATE_FORMATS.ISO);
    },

    addMonths: (date: DateInput, months: number) => {
      const parsed = safeParseDateInput(date);
      if (!parsed) return '';
      return formatInTimeZone(addMonths(parsed, months), CST_TIMEZONE, DATE_FORMATS.ISO);
    },

    subtractMonths: (date: DateInput, months: number) => {
      const parsed = safeParseDateInput(date);
      if (!parsed) return '';
      return formatInTimeZone(subMonths(parsed, months), CST_TIMEZONE, DATE_FORMATS.ISO);
    },
  },

  // Parsing utilities
  parse: {
    fromISO: (isoString: string) => safeParseDateInput(isoString), // Preserve time for parsing
    fromTimestamp: (timestamp: number) => safeParseDateInput(timestamp),
    safe: (dateInput: DateInput) => safeParseDateInput(dateInput),
  },
};

// ===== STREAMLINED EXPORTS =====

// Named exports for tree-shaking
export const {
  ranges,
  format: formatDate,
  validate,
  calculate,
  parse,
  formats,
  today,
  prevMonth,
  yesterday,
  tomorrow,
  prevWeek,
  nextWeek,
  nextMonth,
  todayDisplay,
  todayLong,
  now,
} = dateUtils;

// Default export for convenience
export default dateUtils;

// Type exports
export type DateInput = Date | string | number | null | undefined;
export type DateRange = {
  fromDate: string;
  toDate: string;
};
export type DateFormatKey = keyof typeof DATE_FORMATS;

// ===== SAFE DATE FORMATTING FOR FORMS =====
/**
 * Safely formats a date value for form inputs without timezone conversion
 * Use this instead of format(new Date(value), DATE_FORMAT) to avoid timezone issues
 * @param dateValue - The date value to format (string, Date, number, or null/undefined)
 * @param outputFormat - The desired output format (defaults to DATE_FORMATS.ISO)
 * @returns The formatted date string in the specified format or empty string
 */
export function formatDateForInput(
  dateValue: string | Date | number | null | undefined,
  outputFormat: string = DATE_FORMATS.ISO,
): string {
  if (!dateValue) return '';

  // If already in yyyy-MM-dd format and ISO is requested, return as-is to avoid timezone conversion
  if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue) && outputFormat === DATE_FORMATS.ISO) {
    return dateValue;
  }

  // If it's a Date object, format it
  if (dateValue instanceof Date && isValid(dateValue)) {
    return format(dateValue, outputFormat);
  }

  // If it's a number (timestamp), convert to Date and format it
  if (typeof dateValue === 'number') {
    const dateFromNumber = new Date(dateValue);
    if (isValid(dateFromNumber)) {
      return format(dateFromNumber, outputFormat);
    }
    return '';
  }

  // If it's an ISO string or other format, parse and format it
  if (typeof dateValue === 'string') {
    try {
      const parsed = parseISO(dateValue);
      if (isValid(parsed)) {
        return format(parsed, outputFormat);
      }
    } catch {
      // If parsing fails, return empty string
      return '';
    }
  }

  return '';
}

/**
 * Creates a UTC date from a date string (yyyy-MM-dd format)
 * This ensures the date is stored as UTC midnight
 * @param dateString - The date string in yyyy-MM-dd format
 * @returns A Date object set to UTC midnight
 */
/**
 * Creates a date from a date string (yyyy-MM-dd format)
 * Backend handles CST, so we just parse literally
 * (Name kept for compatibility, but no UTC conversion happens)
 */
export function createUTCDate(dateString: string): Date {
  if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date();
  }

  const [year, month, day] = dateString.split('-').map(Number);
  // Create date in local context (backend handles CST)
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

export function combineDateWithCurrentTime(typedDate: string) {
  const cstNow = getCSTNow();
  const [year, month, day] = typedDate.split('-').map(Number);
  const combined = new Date(year, month - 1, day, cstNow.getHours(), cstNow.getMinutes(), cstNow.getSeconds());
  return combined?.toISOString().slice(0, 19);
}
